/**
 * src/pages/venue/ApprovalQueuePage.tsx — 2304vc
 *
 * Two sections:
 *   1. Needs sending — venue_picks with picker_action = needs_approval
 *      Manager taps → approval form → sends link to act management
 *   2. Awaiting response — approval_requests already sent, pending reply
 */

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Mail, MessageCircle, Phone, ShieldCheck, Send, ChevronRight } from 'lucide-react'
import { fetchApprovalQueue, fetchDigestPicks, createApprovalRequest } from '@/lib/venue'
import { Spinner } from '@/components/ui'
import { cn } from '@/components/ui/utils'
import type { Venue, ApprovalRequest, VenuePick, ApprovalChannel } from '@/types'
import type { PickerSession } from '@/lib/venue'

interface Props { venue: Venue; picker: PickerSession }

const CHANNEL_ICON: Record<ApprovalChannel, typeof Mail> = {
  email: Mail, whatsapp: MessageCircle, sms: Phone,
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1)  return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24)  return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now()
  if (diff <= 0) return 'expired'
  const hrs = Math.floor(diff / 3600_000)
  if (hrs < 24)  return `${hrs}h left`
  return `${Math.floor(hrs / 24)}d left`
}

export function ApprovalQueuePage({ venue, picker }: Props) {
  const queryClient  = useQueryClient()
  const [selected, setSelected] = useState<VenuePick | null>(null)

  // Flagged by picker — needs manager to send approval request
  const { data: allPicks } = useQuery({
    queryKey: ['picker-picks-approval', venue.id],
    queryFn:  () => fetchDigestPicks({ venue_id: venue.id, limit: 100 }),
  })
  const flagged = (allPicks ?? []).filter(p => p.picker_action === 'needs_approval')

  // Already sent — waiting for act response
  const { data: queue, isLoading } = useQuery({
    queryKey: ['venue-approval-queue', venue.id],
    queryFn:  () => fetchApprovalQueue(venue.id),
    staleTime: 5 * 60_000,
  })

  const sentIds = new Set((queue ?? []).map(q => q.pick.media_item_id))
  const unsent  = flagged.filter(p => !sentIds.has(p.media_item_id))

  function thumb(p: VenuePick) {
    const url = p.media?.cloudinary_url
    return url ? url.replace('/upload/', '/upload/w_120,c_fill,q_auto,f_auto/') : null
  }

  return (
    <div className="space-y-6">

      {/* Section 1: Needs sending */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-white">Needs approval</h2>
          {unsent.length > 0 && (
            <span className="text-xs text-amber-400 font-medium">{unsent.length} to send</span>
          )}
        </div>

        {unsent.length === 0 ? (
          <div className="bg-white/3 border border-white/8 rounded-2xl p-5 text-center">
            <ShieldCheck size={20} className="text-white/20 mx-auto mb-2" />
            <p className="text-white/40 text-sm">All flagged shots have been sent for approval</p>
          </div>
        ) : (
          <div className="space-y-2">
            {unsent.map(pick => (
              <button key={pick.id} type="button"
                onClick={() => setSelected(pick)}
                className="w-full flex items-center gap-3 bg-white/4 border border-white/10
                           rounded-xl p-3 hover:bg-white/7 active:scale-99 transition-all text-left">
                {/* Thumb */}
                <div className="w-14 h-16 rounded-lg overflow-hidden shrink-0 bg-white/5">
                  {thumb(pick)
                    ? <img src={thumb(pick)!} alt="" className="w-full h-full object-cover" />
                    : <div className="w-full h-full bg-gradient-to-br from-white/5 to-transparent" />
                  }
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-amber-400 text-[10px] font-semibold uppercase tracking-wider mb-0.5">
                    Approval needed
                  </p>
                  <p className="text-white text-[13px] font-medium leading-snug line-clamp-2">
                    {pick.media?.ai_description ?? 'Flagged shot'}
                  </p>
                  {pick.actioned_at && (
                    <p className="text-white/30 text-[10px] mt-1">Flagged {timeAgo(pick.actioned_at)}</p>
                  )}
                </div>
                <ChevronRight size={14} className="text-white/30 shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Section 2: Awaiting response */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-white">Awaiting response</h2>
          {(queue ?? []).length > 0 && (
            <span className="text-xs text-white/40">{queue!.length} sent</span>
          )}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-6"><Spinner size={20} /></div>
        ) : !queue || queue.length === 0 ? (
          <div className="bg-white/3 border border-white/8 rounded-2xl p-5 text-center">
            <p className="text-white/30 text-sm">No requests sent yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {queue.map(({ request, pick }) => (
              <SentRow key={request.id} request={request} pick={pick} />
            ))}
          </div>
        )}
      </div>

      {/* Send approval modal */}
      {selected && (
        <SendApprovalSheet
          pick={selected}
          pickerId={picker.picker_id}
          onClose={() => setSelected(null)}
          onSent={() => {
            setSelected(null)
            queryClient.invalidateQueries({ queryKey: ['venue-approval-queue', venue.id] })
            queryClient.invalidateQueries({ queryKey: ['picker-picks-approval', venue.id] })
          }}
        />
      )}
    </div>
  )
}

// ── Sent row ──────────────────────────────────────────────────────────────────

function SentRow({ request, pick }: { request: ApprovalRequest; pick: VenuePick }) {
  const [copied, setCopied] = useState(false)
  const ChannelIcon = request.channel ? CHANNEL_ICON[request.channel] : Mail
  const thumbUrl = pick.media?.cloudinary_url
    ? pick.media.cloudinary_url.replace('/upload/', '/upload/w_100,c_fill,q_auto,f_auto/')
    : null

  function copyLink() {
    const url = `${window.location.origin}/approve/${request.access_token}`
    navigator.clipboard?.writeText(url).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <div className="flex items-center gap-3 bg-white/3 border border-white/8 rounded-xl p-3">
      <div className="w-10 h-12 rounded-lg overflow-hidden shrink-0 bg-white/5">
        {thumbUrl && <img src={thumbUrl} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-[13px] font-medium truncate">{request.approver_name}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <ChannelIcon size={10} className="text-white/40" />
          <p className="text-white/40 text-[11px] truncate">{request.approver_contact}</p>
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-[10px] text-white/25">
          <span>{timeAgo(request.created_at)}</span>
          <span>{timeUntil(request.expires_at)}</span>
        </div>
      </div>
      <button type="button" onClick={copyLink}
        className={cn('shrink-0 p-2 rounded-lg border transition-all',
          copied ? 'bg-emerald-500/20 border-emerald-400/40 text-emerald-400'
                 : 'bg-white/5 border-white/10 text-white/40 hover:text-white')}>
        <Copy size={13} />
      </button>
    </div>
  )
}

// ── Send approval sheet ───────────────────────────────────────────────────────

function SendApprovalSheet({ pick, pickerId, onClose, onSent }: {
  pick:     VenuePick
  pickerId: string
  onClose:  () => void
  onSent:   () => void
}) {
  const [name, setName]         = useState('')
  const [contact, setContact]   = useState('')
  const [channel, setChannel]   = useState<ApprovalChannel>('email')
  const [relationship, setRel]  = useState('artist_management')
  const [submitting, setSub]    = useState(false)
  const [error, setError]       = useState<string | null>(null)
  const [sentUrl, setSentUrl]   = useState<string | null>(null)

  const thumbUrl = pick.media?.cloudinary_url
    ? pick.media.cloudinary_url.replace('/upload/', '/upload/w_300,c_fill,q_auto,f_auto/')
    : null

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim() || !contact.trim()) return
    setSub(true); setError(null)
    try {
      const req = await createApprovalRequest({
        pick_id:               pick.id,
        picker_id:             pickerId,
        approver_name:         name.trim(),
        approver_contact:      contact.trim(),
        channel,
        approver_relationship: relationship as import('@/types').ApproverRelationship,
        expires_in_hours:      72,
      })
      const url = `${window.location.origin}/approve/${req.access_token}`
      setSentUrl(url)
      onSent()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send')
    }
    setSub(false)
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/60" onClick={onClose}>
      <div className="bg-black border-t border-white/10 rounded-t-3xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}>

        {/* Handle */}
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto mt-3 mb-4" />

        {/* Photo context */}
        <div className="flex items-center gap-3 px-5 pb-4 border-b border-white/8">
          <div className="w-12 h-14 rounded-lg overflow-hidden shrink-0 bg-white/5">
            {thumbUrl && <img src={thumbUrl} alt="" className="w-full h-full object-cover" />}
          </div>
          <div>
            <p className="text-amber-400 text-[10px] font-semibold uppercase tracking-wider">Send for approval</p>
            <p className="text-white text-sm leading-snug line-clamp-2 mt-0.5">
              {pick.media?.ai_description ?? 'Flagged shot'}
            </p>
          </div>
        </div>

        {sentUrl ? (
          <div className="px-5 py-6 space-y-4">
            <div className="bg-emerald-500/15 border border-emerald-400/30 rounded-2xl p-4 text-center">
              <p className="text-emerald-400 font-semibold mb-1">Request created</p>
              <p className="text-white/50 text-xs mb-3">Copy this link and send it to the approver</p>
              <p className="text-white/70 text-xs break-all font-mono bg-white/5 rounded-xl p-2">{sentUrl}</p>
            </div>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(sentUrl); onClose() }}
              className="w-full py-3 rounded-2xl bg-white text-black font-bold text-sm">
              Copy link and close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="px-5 py-4 space-y-4">
            {/* Name */}
            <label className="block">
              <span className="text-xs text-white/40 uppercase tracking-wider font-semibold block mb-1.5">
                Approver name *
              </span>
              <input type="text" value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Purple Wall Music"
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3
                           text-white placeholder-white/25 focus:outline-none focus:border-white/30" />
            </label>

            {/* Relationship */}
            <div>
              <span className="text-xs text-white/40 uppercase tracking-wider font-semibold block mb-2">Relationship</span>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'artist_management', label: 'Artist management' },
                  { value: 'artist_directly',   label: 'Artist directly' },
                  { value: 'venue_marketing',    label: 'Venue marketing' },
                  { value: 'other',              label: 'Other' },
                ].map(r => (
                  <button key={r.value} type="button" onClick={() => setRel(r.value)}
                    className={cn('py-2.5 px-3 rounded-xl border text-left text-[13px] font-medium transition-all',
                      relationship === r.value
                        ? 'bg-amber-500/15 border-amber-400/40 text-amber-300'
                        : 'bg-white/4 border-white/10 text-white/60 hover:bg-white/7')}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Channel */}
            <div>
              <span className="text-xs text-white/40 uppercase tracking-wider font-semibold block mb-2">Send via</span>
              <div className="grid grid-cols-3 gap-2">
                {([['email','Email',Mail],['whatsapp','WhatsApp',MessageCircle],['sms','SMS',Phone]] as const).map(([ch, label, Icon]) => (
                  <button key={ch} type="button" onClick={() => setChannel(ch as ApprovalChannel)}
                    className={cn('py-2.5 rounded-xl border flex flex-col items-center gap-1 text-[12px] font-medium transition-all',
                      channel === ch
                        ? 'bg-white/15 border-white/30 text-white'
                        : 'bg-white/4 border-white/10 text-white/50 hover:bg-white/7')}>
                    <Icon size={14} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact */}
            <label className="block">
              <span className="text-xs text-white/40 uppercase tracking-wider font-semibold block mb-1.5">
                {channel === 'email' ? 'Email address' : 'Phone number'} *
              </span>
              <input type={channel === 'email' ? 'email' : 'tel'} value={contact}
                onChange={e => setContact(e.target.value)}
                placeholder={channel === 'email' ? 'Enter email address' : 'Enter phone number'}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3
                           text-white placeholder-white/25 focus:outline-none focus:border-white/30" />
            </label>

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <button type="submit"
              disabled={submitting || !name.trim() || !contact.trim()}
              className="w-full py-3.5 rounded-2xl font-bold text-sm transition-all
                         bg-white text-black disabled:bg-white/10 disabled:text-white/30
                         flex items-center justify-center gap-2">
              <Send size={14} />
              {submitting ? 'Sending…' : 'Send approval request'}
            </button>

            <button type="button" onClick={onClose}
              className="w-full py-2 text-white/30 text-sm hover:text-white/50 transition-colors">
              Cancel
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
